// "In your pocket" app-preview section — copy on the left, fake iPhone
// frame on the right showing a sample invoice. Ported from the design's
// `direction-editorial.jsx` `AppPreview` component.

import { NalaIcon } from './NalaIcon'
import { nala } from './palette'

const FEATURES = [
  { t: 'Visit notes you can actually read', b: 'Plain-language summaries written by your vet.' },
  { t: 'Care that continues',               b: 'Delivered to your door in-between visits.' },
  { t: 'Insurance-ready invoices',          b: 'Itemized PDFs that work with every major insurer.' },
]

const INVOICE_LINES: [string, string][] = [
  ['Home visit / exam (recheck)', '$90.00'],
  ['Ultrasound',                  '$420.00'],
]

export default function AppPreview() {
  return (
    <div
      className="mt-16 rounded-[28px] grid items-center gap-8 lg:gap-12 px-4 lg:px-8"
      style={{
        background: nala.soft,
        paddingTop: '48px',
        paddingBottom: '48px',
        gridTemplateColumns: '1fr',
      }}
    >
      <div className="flex flex-col gap-8 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
        {/* Copy column */}
        <div>
          <span
            className="italic text-base"
            style={{ color: nala.clay, fontFamily: nala.display }}
          >
            In your pocket
          </span>
          <h3
            className="mt-3 mb-3 text-3xl sm:text-4xl"
            style={{
              fontFamily: nala.display,
              fontWeight: 400,
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
            }}
          >
            Every visit, neatly kept.
          </h3>
          <p
            className="text-base leading-relaxed max-w-md"
            style={{ color: nala.mute }}
          >
            After we leave, your dashboard is updated within hours. Full visit
            notes, follow-up care, and a clean itemized invoice for
            insurance. No paper, no waiting on the phone.
          </p>
          <div className="mt-5 flex flex-col gap-2.5">
            {FEATURES.map((f) => (
              <div key={f.t} className="flex items-start gap-3">
                <div
                  className="flex shrink-0 items-center justify-center rounded-full mt-0.5"
                  style={{
                    width: 22,
                    height: 22,
                    background: nala.accent,
                    color: nala.accentInk,
                  }}
                >
                  <NalaIcon.Check size={11} color={nala.accentInk} />
                </div>
                <div>
                  <div
                    className="text-base font-medium"
                    style={{ fontFamily: nala.display }}
                  >
                    {f.t}
                  </div>
                  <div className="text-sm" style={{ color: nala.mute }}>
                    {f.b}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Phone mock */}
        <div className="flex justify-center mt-0">
          <div
            className="relative w-full lg:w-auto"
            style={{
              maxWidth: 320,
              aspectRatio: '320 / 620',
              borderRadius: 44,
              background: '#1c1916',
              padding: 10,
              boxShadow:
                '0 30px 70px rgba(40,30,20,0.22), 0 6px 18px rgba(40,30,20,0.12)',
            }}
          >
            <div
              className="flex flex-col h-full overflow-hidden"
              style={{ background: nala.bg, borderRadius: 36 }}
            >
              {/* notch */}
              <div
                className="absolute z-[2]"
                style={{
                  top: 22,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 96,
                  height: 24,
                  borderRadius: 14,
                  background: '#1c1916',
                }}
              />
              <div
                className="flex justify-between text-[11px] font-semibold pt-3.5 px-5 pb-2"
                style={{ color: nala.ink, fontFamily: nala.sans }}
              >
                <span>9:41</span>
                <span>···</span>
              </div>
              <div
                className="px-5 pb-3 pt-3.5"
                style={{ borderBottom: `1px solid ${nala.line}` }}
              >
                <div
                  className="text-[11px] uppercase"
                  style={{
                    color: nala.mute,
                    letterSpacing: '0.06em',
                    fontFamily: nala.sans,
                  }}
                >
                  Monday, April 13
                </div>
                <div
                  className="text-[22px] font-medium mt-0.5"
                  style={{ fontFamily: nala.display }}
                >
                  Waffles
                </div>
                <div
                  className="flex items-center gap-2 mt-1 text-xs"
                  style={{ color: nala.mute }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: nala.ok }}
                  />
                  <span>Dr. Smith</span>
                </div>
              </div>
              <div
                className="flex pt-2 px-3.5 gap-1"
                style={{ borderBottom: `1px solid ${nala.line}` }}
              >
                {[
                  { id: 'notes',     label: 'Visit notes' },
                  { id: 'aftercare', label: 'After care' },
                  { id: 'invoice',   label: 'Invoice' },
                ].map((tab) => {
                  const active = tab.id === 'invoice'
                  return (
                    <div
                      key={tab.id}
                      className="px-3 py-2.5 text-xs font-semibold -mb-px"
                      style={{
                        fontFamily: nala.sans,
                        color: active ? nala.ink : nala.mute,
                        borderBottom: `2px solid ${active ? nala.accent : 'transparent'}`,
                      }}
                    >
                      {tab.label}
                    </div>
                  )
                })}
              </div>
              <div
                className="flex-1 px-5 py-5 text-sm overflow-hidden"
                style={{ color: nala.ink, lineHeight: 1.5 }}
              >
                <div
                  className="text-[11px] uppercase mb-2 font-semibold"
                  style={{
                    color: nala.mute,
                    fontFamily: nala.sans,
                    letterSpacing: '0.04em',
                  }}
                >
                  Invoice #2566
                </div>
                <div className="flex flex-col gap-1.5 text-[12.5px]">
                  {INVOICE_LINES.map(([label, amount]) => (
                    <div
                      key={label}
                      className="flex justify-between py-1.5"
                      style={{ borderBottom: `1px dashed ${nala.line}` }}
                    >
                      <span>{label}</span>
                      <span>{amount}</span>
                    </div>
                  ))}
                </div>
                <div
                  className="flex justify-between mt-3 text-lg font-medium"
                  style={{ fontFamily: nala.display }}
                >
                  <span>Total</span>
                  <span>$510.00</span>
                </div>
                <button
                  type="button"
                  className="mt-3.5 w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm"
                  style={{
                    background: nala.accent,
                    color: nala.accentInk,
                    padding: '12px 18px',
                  }}
                >
                  <NalaIcon.Arrow size={12} color={nala.accentInk} />
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
